/**
 * Джерело ліда для форм лендінгів.
 *
 * Було: utm_tags відправлялись ЛИШЕ з офлайн-лендінгу (typeSite === 'offline'), і тільки якщо
 * мітки стояли в URL. Для espanol/deutsch/online/kids не відправлялось нічого, а бекенд
 * (CreateRequest.php) підставляв свій запасний варіант по заголовку Referer — але заголовок
 * при запиті на власний /api дорівнює адресі самого лендінгу, тож усе падало в останній else
 * і кожен лід ставав фейковим «referral». Тепер джерело визначає браузер і шле його завжди.
 *
 * Класифікація — дзеркало основного сайту englishprime.ua (attribution.ts / detectChannel).
 */

const SEARCH = ['google', 'bing', 'yahoo', 'duckduckgo', 'ecosia', 'yandex', 'ukr.net']
const SOCIAL: Array<[string, string]> = [
  ['instagram', 'instagram'],
  ['facebook', 'facebook'],
  ['fb.com', 'facebook'],
  ['tiktok', 'tiktok'],
  ['t.co', 'twitter'],
  ['telegram', 'telegram'],
  ['t.me', 'telegram'],
  ['youtube', 'youtube'],
  ['linkedin', 'linkedin'],
]
const AI = ['chatgpt', 'openai', 'perplexity', 'gemini', 'copilot', 'claude.ai']

const STORAGE_KEY = 'ep_markers'

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}

/**
 * Збирає utm_tags для мутації createRequest.
 * Пріоритет: мітки з URL → clickid → реальний document.referrer → прямий захід.
 * Повертає об'єкт, у якому utm_source/utm_medium заповнені ЗАВЖДИ, щоб бекенд не вмикав
 * свій зламаний фолбек по Referer.
 */
export const buildUtmTags = (markers?: string | null): Record<string, string> => {
  let raw = markers ?? ''

  // мітки живуть у пам'яті сторінки; перезавантаження або перехід без параметрів їх губили
  try {
    if (raw) sessionStorage.setItem(STORAGE_KEY, raw)
    else raw = sessionStorage.getItem(STORAGE_KEY) ?? ''
  } catch {
    // приватний режим / вимкнене сховище — просто працюємо без нього
  }

  const params: Record<string, string> = Object.fromEntries(new URLSearchParams(raw).entries())

  if (params.utm_source) return params

  if (params.gclid || params.gbraid || params.gad_source) {
    params.utm_source = 'google'
    params.utm_medium = 'cpc'
    return params
  }
  if (params.fbclid) {
    params.utm_source = 'facebook'
    params.utm_medium = 'cpc'
    return params
  }

  const ref = typeof document !== 'undefined' ? document.referrer || '' : ''
  const host = hostOf(ref)
  const self = typeof window !== 'undefined' ? window.location.hostname.replace(/^www\./, '') : ''

  if (!host || host === self) {
    params.utm_source = 'direct'
    params.utm_medium = 'none'
  } else if (SEARCH.some((s) => host.includes(s))) {
    params.utm_source = host
    params.utm_medium = 'organic'
  } else if (AI.some((s) => host.includes(s))) {
    params.utm_source = host
    params.utm_medium = 'referral'
  } else {
    const social = SOCIAL.find(([needle]) => host.includes(needle))
    if (social) {
      params.utm_source = social[1]
      params.utm_medium = 'social'
    } else {
      params.utm_source = host
      params.utm_medium = 'referral'
    }
  }

  return params
}
